<template>
  <div v-if="emptyState" class="md:max-w-screen-lg px-2 md:px-0 mx-auto">
    <div class="py-3 md:py-5">
      <div class="">
        <h2 class="text-xl md:text-6xl py-1 md:py-2 text-brand-blue">Have a team?</h2>
        <h2 class="text-xl md:text-6xl py-1 md:py-2 text-brand-black">Create brand consistency</h2>
        <h2 class="text-xl md:text-6xl py-1 md:py-2 text-brand-black">
          & get organizational oversight
        </h2>
      </div>

      <div class="hidden md:flex flex-row justify-between py-5">
        <div class="flex flex-col px-1" v-for="feature in features" :key="feature.label">
          <h5 class="py-1 font-semibold">{{ feature.label }}</h5>
          <p class="py-1 text-brand-dark-gray text-sm">
            {{ feature.detail }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex md:hidden flex-col py-3">
      <div
        class="flex flex-col py-2 cursor-pointer border-b border-brand-light-gray"
        v-for="(feature, index) in features"
        :key="`faq-${index}`"
        @click="toggleFeature(index)"
      >
        <div class="flex flex-row justify-between border-b border-brand-mid-gray pb-2">
          <div class="flex flex-1 flex-row items-center">
            <p class="text-sm md:text-lg">{{ feature.label }}</p>
          </div>
          <div class="flex flex-row items-center">
            <PlusIcon
              class="w-5 h-5 text-brand-green transform transition duration-400 ease-in-out"
              :class="[feature.open ? 'rotate-45' : '']"
            />
          </div>
        </div>

        <div class="py-3 px-1 md:px-8" v-if="feature.open">
          <p class="whitespace-pre-line text-xs md:text-base">
            {{ feature.detail }}
          </p>
        </div>
      </div>
    </div>

    <div class="hidden md:flex flex-col rounded mb-5 py-3 px-3 md:py-5 md:px-10 bg-white">
      <div class="flex flex-row justify-between">
        <div class="flex items-center">
          <h2 class="text-xl py-5 text-bramd-black">Your team here</h2>
        </div>
        <div class="flex items-center">
          <PrimaryButton :disabled="true">Add user</PrimaryButton>
        </div>
      </div>

      <Line />

      <div class="flex flex-row py-3">
        <div class="w-1/5">
          <h5>Name</h5>
        </div>

        <div class="w-1/5">
          <h5>Email address</h5>
        </div>

        <div class="w-1/5">
          <h5>Agent Dropdown</h5>
        </div>

        <div class="w-1/5">
          <h5>Apps received</h5>
        </div>

        <div class="w-1/5">
          <h5>Last login</h5>
        </div>
      </div>
    </div>

    <div class="py-3 md:py-5">
      <div class="">
        <h2 class="text-xl md:text-5xl py-1 md:py-2 font-semibold text-brand-black">
          Contact us to find out how you can get
        </h2>
        <h2 class="text-xl md:text-5xl py-1 md:py-2 font-semibold text-brand-blue">
          the most return for your team
        </h2>
      </div>
    </div>

    <div class="flex flex-col rounded mb-5 py-3 px-3 md:py-5 md:px-10 bg-white">
      <div class="flex flex-col md:flex-row py-1">
        <div class="flex flex-col w-full md:w-2/3 md:mr-5">
          <label for="name" class="block">
            Team name
            <span class="text-brand-red">*</span>
          </label>
          <SingleLineInput
            id="name"
            type="text"
            placeholder="Enter your team name"
            :value="name"
            @input="name = $event.target.value"
          ></SingleLineInput>
        </div>

        <div class="flex flex-col w-full md:w-1/3">
          <label for="numAgents" class="block">
            # of agents
            <span class="text-brand-red">*</span>
          </label>
          <SingleLineInput
            id="numAgents"
            type="number"
            placeholder="Enter the # of agents"
            :value="`${numAgents}`"
            @input="numAgents = parseInt($event.target.value)"
          ></SingleLineInput>
        </div>
      </div>

      <div class="flex flex-col py-1">
        <label for="customMessage" class="block"> Attach a message </label>
        <MultiLineInput
          id="message"
          placeholder="Type to enter your message"
          :value="message"
          @input="message = $event.target.value"
        ></MultiLineInput>
      </div>

      <div class="flex flex-row justify-center py-3">
        <div class="w-full md:w-1/3">
          <PrimaryButton
            @click="sendMessage"
            :disabled="isCompanyLoading"
            :is-loading="isCompanyLoading"
            >Schedule a call</PrimaryButton
          >
        </div>
      </div>
    </div>
  </div>
  <template v-else>
    <template v-if="isCompanyLoading">
      <Loading />
    </template>
    <template v-else>
      <div v-for="company in getCompanies" :key="company.id" class="pb-5 md:pb-10">
        <div class="flex flex-col rounded my-3 md:mb-5 py-3 px-3 md:py-5 md:px-10 bg-white">
          <div class="flex flex-col md:flex-row justify-between">
            <div class="flex items-center">
              <h2 class="text-xl py-5 text-bramd-black">{{ company.name }}</h2>
            </div>
            <div class="flex items-center">
              <PrimaryButton @click="openAddUserModal(company)">Add user</PrimaryButton>
            </div>
          </div>

          <Line class="hidden md:flex" />

          <div class="hidden md:flex flex-row py-3">
            <div class="w-1/5">
              <h5>Name</h5>
            </div>

            <div class="w-1/5">
              <h5>Email address</h5>
            </div>

            <div class="w-1/5">
              <h5>Agent Dropdown</h5>
            </div>

            <div class="w-1/5">
              <h5>Apps received</h5>
            </div>

            <div class="w-1/5">
              <h5>Last login</h5>
            </div>
          </div>
        </div>

        <div
          v-for="member in company.memberships"
          :key="member.id"
          class="flex flex-row rounded bg-white py-3 my-3 md:py-5 md:px-10"
        >
          <div class="w-full hidden md:flex flex-row">
            <div class="w-1/5">
              <p
                class="text-brand-green hover:underline cursor-pointer truncate"
                @click="openMemberProfile(company, member)"
              >
                {{ member.name || "No name" }}
              </p>
            </div>

            <div class="w-1/5">
              <p class="truncate">{{ member.user.username }}</p>
            </div>

            <div class="w-1/5">
              <CheckIcon class="text-brand-green w-8 h-8 pl-2" v-if="member.is_agent" />
              <XIcon class="text-brand-red w-8 h-8" v-else />
            </div>

            <div class="w-1/5">
              <p>{{ member.apps_received }}</p>
            </div>

            <div class="w-1/5">
              <p>{{ member.user.last_login || "N/A" }}</p>
            </div>
          </div>

          <div class="w-full flex md:hidden flex-col px-2">
            <div class="flex flex-row justify-between">
              <div class="flex flex-col w-1/2">
                <p class="py-1">
                  {{ member.name || "No name" }}
                </p>
                <p class="text-xs text-brand-mid-gray">
                  {{ member.user.username }}
                </p>
                <div class="flex flex-row py-1">
                  <p>Agent dropdown</p>
                  <div class="flex items-center ml-1">
                    <CheckIcon class="text-brand-green w-5 h-5" v-if="member.is_agent" />
                    <XIcon class="text-brand-red w-5 h-5" v-else />
                  </div>
                </div>
                <p class="py-1">Apps received: {{ member.apps_received }}</p>
                <p class="py-1">Last login: {{ member.last_login || "N/A" }}</p>
              </div>
              <div class="flex flex-col w-1/2 mt-3 items-end">
                <DotsVerticalIcon
                  class="w-7 h-7 text-brand-mid-gray"
                  @click="showMemberProfile(member)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </template>

  <UserModal
    :company="currentCompany"
    :membership="currentMember"
    :mode="mode"
    :showModal="showUserModal"
    @close="closeUserModal"
  />

  <ActionToast ref="teamsToast" />
</template>
<script>
import { mapGetters } from "vuex"

import UserModal from "./components/UserModal.vue"

import { PlusIcon, CheckIcon, XIcon, DotsVerticalIcon } from "@heroicons/vue/outline"

import { COMPANY_GET_LIST, COMPANY_CONTACT_SALES } from "@/store/types"

export default {
  components: {
    PlusIcon,
    CheckIcon,
    XIcon,
    DotsVerticalIcon,
    UserModal
  },
  computed: {
    ...mapGetters(["getCurrentUser", "getCompanies", "isCompanyLoading"])
  },
  data() {
    return {
      emptyState: true,
      showUserModal: false,
      showMemberProfile: false,
      currentMember: null,
      currentCompany: null,
      mode: null,
      name: "",
      numAgents: 0,
      message: "",
      features: [
        {
          label: "Establish a process",
          detail: "Our concierge will help set up a uniform process across all agents."
        },
        {
          label: "Full control",
          detail: "View all incoming applications, add and remove agents, and earn more."
        },
        {
          label: "Customized set-up",
          detail: "Create permissions and structure that works for your company."
        },
        {
          label: "No added costs",
          detail: " Get all the benefits and increase returns with no added costs."
        }
      ]
    }
  },
  mounted() {},
  watch: {
    getCurrentUser: {
      handler(val) {
        if (val) {
          this.emptyState = !val.landlord.in_company || !val.can_admin_company

          if (!this.emptyState && !this.getCompanies) {
            this.$store.dispatch(COMPANY_GET_LIST)
          }
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    async sendMessage() {
      const { name, numAgents, message } = this
      if (!name || !numAgents) {
        return
      }

      await this.$store.dispatch(COMPANY_CONTACT_SALES, {
        name,
        numAgents,
        message
      })

      this.name = ""
      this.numAgents = 0
      this.message = ""

      this.$refs.teamsToast.show({
        type: "success",
        hasClose: true,
        content: "Message sent!",
        icon: "check"
      })
    },
    toggleFeature(index) {
      this.features[index].open = !this.features[index].open
    },
    openAddUserModal(company) {
      this.showUserModal = true
      this.mode = "add"
      this.currentCompany = company
    },
    closeUserModal() {
      this.showUserModal = false
      this.currentCompany = null
      this.currentMember = null
      this.mode = null
    },
    openMemberProfile(company, member) {
      this.currentCompany = company
      this.currentMember = member
      this.showUserModal = true
      this.mode = "update"
    }
  }
}
</script>
